/**
 * AttachmentList
 *
 * Renders an event's attachments: images as a thumbnail grid with lightbox
 * (reusing ImageMessage), documents as download chips. Domain-agnostic — used
 * on any event card and inside AttachmentPicker (with onRemove).
 */

import {
  DocumentIcon,
  DocumentTextIcon,
  TableCellsIcon,
  PresentationChartBarIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useState } from 'react';
import ImageMessage from '../../features/messaging/components/ImageMessage';
import MediaUploadService from '../../services/messaging/MediaUploadService';
import IncidentLinkAction from '../../features/incidents/components/IncidentLinkAction';

function DocIcon({ mimeType }) {
  const kind = MediaUploadService.getFileType(mimeType);
  const cls = 'w-5 h-5 text-base-content/60 shrink-0';
  if (kind === 'pdf' || kind === 'doc') return <DocumentTextIcon className={cls} />;
  if (kind === 'spreadsheet') return <TableCellsIcon className={cls} />;
  if (kind === 'presentation') return <PresentationChartBarIcon className={cls} />;
  return <DocumentIcon className={cls} />;
}

const isImageAttachment = (attachment) => (
  attachment?.kind === 'image'
  || attachment?.mimeType?.startsWith('image/')
  || /\.(png|jpe?g|webp|gif|bmp|avif|heic|heif|svg)$/i.test(attachment?.fileName || '')
);

const fileTypeLabel = (attachment) => {
  const extension = attachment?.fileName?.split('.').pop()?.toUpperCase();
  if (extension && extension.length <= 5) return extension;
  const kind = MediaUploadService.getFileType(attachment?.mimeType);
  return kind === 'doc' ? 'DOC' : 'FILE';
};

const fileTypeClasses = (attachment) => {
  const type = fileTypeLabel(attachment);
  if (type === 'PDF') return 'bg-error/10 text-error';
  if (type === 'DOC' || type === 'DOCX') return 'bg-info/10 text-info';
  return 'bg-base-200 text-base-content/60';
};

function CompactTypedTile({ attachment }) {
  return (
    <div className={`flex h-full w-full flex-col items-center justify-center p-2 text-center ${fileTypeClasses(attachment)}`}>
      <DocumentTextIcon className="h-6 w-6" />
      <span className="mt-1 text-[10px] font-semibold tracking-wide">{fileTypeLabel(attachment)}</span>
      <span className="mt-1 w-full truncate text-[10px] font-normal normal-case tracking-normal text-base-content/60">
        {attachment.fileName}
      </span>
      <span className="mt-0.5 text-[9px] font-normal normal-case tracking-normal text-base-content/45">
        {MediaUploadService.formatFileSize(attachment.fileSize)}
      </span>
    </div>
  );
}

function CompactAttachmentTile({ attachment }) {
  const [imageFailed, setImageFailed] = useState(false);
  const imageUrl = attachment.thumbnailUrl || attachment.url;
  const showImage = isImageAttachment(attachment) && imageUrl && !imageFailed;

  return (
    <a
      href={attachment.url || attachment.thumbnailUrl}
      target="_blank"
      rel="noopener noreferrer"
      download={!isImageAttachment(attachment) ? attachment.fileName : undefined}
      className="block h-24 w-24 shrink-0 overflow-hidden rounded-xl border border-base-300 bg-base-200"
      aria-label={`Open ${attachment.fileName || 'attachment'}`}
      title={[attachment.fileName, MediaUploadService.formatFileSize(attachment.fileSize)].filter(Boolean).join(' · ')}
      data-attachment-preview-tile
    >
      {showImage ? (
        <img
          src={imageUrl}
          alt=""
          className="h-full w-full object-cover"
          onError={() => setImageFailed(true)}
        />
      ) : (
        <CompactTypedTile attachment={attachment} />
      )}
    </a>
  );
}

export default function AttachmentList({ attachments, readOnly = true, onRemove, disabled = false, sourceRef = null, compact = false }) {
  if (!attachments?.length) return null;

  if (compact) {
    return (
      <div
        className="flex w-full min-w-0 gap-2 overflow-x-auto pb-1"
        aria-label="Included attachments"
      >
        {attachments.map(attachment => (
          <CompactAttachmentTile key={attachment.id} attachment={attachment} />
        ))}
      </div>
    );
  }

  const images = attachments.filter(isImageAttachment);
  const documents = attachments.filter((attachment) => !isImageAttachment(attachment));

  return (
    <div className="flex flex-col gap-3">
      {images.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {images.map((att) => (
            <div key={att.id} className="relative">
              <div className="relative shrink-0">
                <ImageMessage media={att} />
                {readOnly && sourceRef && (
                  <div className="absolute bottom-1 right-1 rounded-full bg-base-100/90 shadow-sm">
                    <IncidentLinkAction
                      compact
                      reference={{
                        ...sourceRef,
                        attachmentId: att.id,
                        fileName: att.fileName || 'Image',
                        label: att.fileName || 'Image',
                      }}
                    />
                  </div>
                )}
                {!readOnly && onRemove && (
                  <button
                    type="button"
                    className="absolute -right-2 -top-2 btn btn-circle btn-xs border border-base-300 bg-base-100 text-base-content/60 shadow-sm hover:bg-base-200"
                    onClick={() => onRemove(att.id)}
                    disabled={disabled}
                    aria-label={`Remove ${att.fileName || 'image'}`}
                  >
                    <XMarkIcon className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {documents.length > 0 && (
        <div className="flex flex-col gap-2">
          {documents.map((att) => (
            <div
              key={att.id}
              className="flex items-center gap-3 rounded-lg bg-base-200 p-3"
            >
              <DocIcon mimeType={att.mimeType} />
              <a
                href={att.url}
                target="_blank"
                rel="noopener noreferrer"
                download={att.fileName}
                className="flex-1 min-w-0 link link-hover"
              >
                <div className="truncate text-sm font-medium">{att.fileName}</div>
                <div className="text-xs text-base-content/60">
                  {MediaUploadService.formatFileSize(att.fileSize)}
                </div>
              </a>
              {readOnly && sourceRef && (
                <IncidentLinkAction
                  compact
                  reference={{
                    ...sourceRef,
                    attachmentId: att.id,
                    fileName: att.fileName || 'Document',
                    label: att.fileName || 'Document',
                  }}
                />
              )}
              {!readOnly && onRemove && (
                <button
                  type="button"
                  className="btn btn-ghost btn-xs btn-circle"
                  onClick={() => onRemove(att.id)}
                  disabled={disabled}
                  aria-label={`Remove ${att.fileName || 'document'}`}
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
